import { useJobillaOrgId } from '@/hooks'
import { cn } from '@/lib/utils'
import { useWhatsAppEnabled } from '@/ui/utilities/hooks/use-whatsapp-enabled'
import { logger } from '@/ui/utilities/instrumentation/logger'
import { formatInactivityDate } from '@/ui/views/settings/sub-pages/whatsapp-settings/inactivity-state'
import { useWhatsAppAccountInactivity } from '@jobilla/nexus-sdk'
import { getRouteApi, Link } from '@tanstack/react-router'
import { AlertTriangleIcon, XIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
	inactivityDaysLeft,
	type InactivityTargetCampaign,
	pickInactivityTarget,
} from './whatsapp-inactivity-alert-target'
import { inactivityAlertDismissalKey, shouldShowInactivityAlert } from './whatsapp-inactivity-alert-visibility'

const companyRoute = getRouteApi('/_auth/company/$companyId')

function isStoredInSession(key: string): boolean {
	try {
		return sessionStorage.getItem(key) !== null
	} catch {
		return false
	}
}

function storeInSession(key: string): void {
	try {
		sessionStorage.setItem(key, '1')
	} catch {
		// storage unavailable
	}
}

function trackViewOncePerSession(organisationId: string, scheduledDeactivationAt: string, daysLeft: number): void {
	const key = `whatsapp-inactivity-viewed:${organisationId}:${scheduledDeactivationAt}`
	if (isStoredInSession(key)) { return }
	storeInSession(key)
	logger.track('WhatsApp: Inactivity banner viewed', { data: { days_left: daysLeft } })
}

export function WhatsAppInactivityAlert({
	campaigns,
	className,
}: {
	campaigns: InactivityTargetCampaign[] | null
	className?: string
}) {
	const { t, i18n } = useTranslation('common')
	const organisationId = useJobillaOrgId()
	const { companyId } = companyRoute.useParams()
	const whatsappEnabled = useWhatsAppEnabled()
	const [dismissedKey, setDismissedKey] = useState<string | null>(null)

	const { data } = useWhatsAppAccountInactivity(organisationId, {
		enabled: !!organisationId && whatsappEnabled,
		staleTime: 5 * 60 * 1000,
	})
	const scheduledDeactivationAt = data?.scheduledDeactivationAt ?? null

	const visible = !!organisationId && shouldShowInactivityAlert({
		organisationId,
		scheduledDeactivationAt,
		whatsappEnabled,
		isDismissed: (key) => key === dismissedKey || isStoredInSession(key),
	})
	const daysLeft = scheduledDeactivationAt ? inactivityDaysLeft(scheduledDeactivationAt) : 0

	useEffect(() => {
		if (!visible || !organisationId || !scheduledDeactivationAt) { return }
		trackViewOncePerSession(organisationId, scheduledDeactivationAt, daysLeft)
	}, [visible, organisationId, scheduledDeactivationAt, daysLeft])

	if (!visible || !organisationId || !scheduledDeactivationAt) { return null }

	const target = campaigns ? pickInactivityTarget(campaigns) : null

	const trackClick = (kind: 'campaign' | 'create_campaign') => {
		logger.track('WhatsApp: Inactivity banner clicked', { data: { target: kind, days_left: daysLeft } })
	}

	const dismiss = () => {
		const key = inactivityAlertDismissalKey(organisationId, scheduledDeactivationAt)
		storeInSession(key)
		setDismissedKey(key)
		logger.track('WhatsApp: Inactivity banner dismissed', { data: { days_left: daysLeft } })
	}

	const buttonClassName =
		'shrink-0 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700 transition-colors'

	return (
		<div
			role="alert"
			className={cn(
				'flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3',
				className,
			)}
		>
			<AlertTriangleIcon className="size-5 shrink-0 text-amber-600" aria-hidden />
			<div className="flex-1 text-sm text-amber-900">
				{t('whatsappSettings.inactivity.warning', {
					date: formatInactivityDate(scheduledDeactivationAt, i18n.language),
				})}
			</div>
			{target?.kind === 'campaign' && (
				<Link
					to="/company/$companyId/$campaignId/candidates"
					params={{ companyId, campaignId: target.campaignId }}
					onClick={() => trackClick('campaign')}
					className={buttonClassName}
				>
					{t('whatsappSettings.inactivity.messageCandidate')}
				</Link>
			)}
			{target?.kind === 'create_campaign' && (
				<Link
					to="/company/$companyId/campaign/new"
					params={{ companyId }}
					onClick={() => trackClick('create_campaign')}
					className={buttonClassName}
				>
					{t('whatsappSettings.inactivity.createCampaign')}
				</Link>
			)}
			<button
				type="button"
				onClick={dismiss}
				aria-label={t('common.dismiss')}
				className="shrink-0 rounded-md p-1 text-amber-700 hover:bg-amber-100 transition-colors"
			>
				<XIcon className="size-4" />
			</button>
		</div>
	)
}
